'use client';

import { useTranslations } from 'next-intl';
import { ComponentProps, createContext, useContext } from 'react';
import GoogleFullLogo from '@/assets/images/google-full-logo.svg';
import TrustpilotFullLogo from '@/assets/images/trustpilot-full-logo.svg';
import { cn } from '@/shared/lib/cn';
import { Layout } from '@/shared/ui/layout';
import './contact.scss';
import { ReviewPlatform } from './types';

const getReviewPlatformIcon = (platform: ReviewPlatform) => {
    switch (platform) {
        case ReviewPlatform.GOOGLE:
            return GoogleFullLogo;
        case ReviewPlatform.TRUSTPILOT:
            return TrustpilotFullLogo;
    }
};

type ContactReviewBoxContextValue = {
    review?: string;
    platforms?: ReviewPlatform[];
    title?: string;
};

const ContactReviewBoxContext = createContext<ContactReviewBoxContextValue>(
    {} as ContactReviewBoxContextValue,
);

type ContactReviewBoxWrapperProps = ComponentProps<typeof Layout> & {
    review: string;
    platforms: ReviewPlatform[];
};

export const ContactReviewBoxWrapper = ({
    review,
    platforms,
    children,
    ...props
}: ContactReviewBoxWrapperProps) => {
    const t = useTranslations();

    const contextValue: ContactReviewBoxContextValue = {
        review,
        platforms,
        title: t('CONTACT.REVIEW_BOX.TITLE'),
    };

    return (
        <ContactReviewBoxContext.Provider value={contextValue}>
            <Layout className="card contact__box" {...props}>
                {children}
            </Layout>
        </ContactReviewBoxContext.Provider>
    );
};

type ContactReviewBoxTitleProps = ComponentProps<typeof Layout>;

export const ContactReviewBoxTitle = ({ media, ...props }: ContactReviewBoxTitleProps) => {
    const context = useContext(ContactReviewBoxContext);
    return (
        <Layout className={cn('contact__box-title', 'contact__review-box__title')} media={media} {...props}>
            <span data-testid="contact-review-box-title">{context.title}</span>
            <span data-testid="contact-review-box-review">{context.review}</span>
        </Layout>
    );
};

type ContactReviewBoxDividerProps = ComponentProps<typeof Layout>;

export const ContactReviewBoxDivider = ({ media, ...props }: ContactReviewBoxDividerProps) => {
    return <Layout element="div" className="contact__divider" media={media} {...props} />;
};

type ContactReviewBoxPlatformsProps = ComponentProps<typeof Layout> & {
    platforms?: ReviewPlatform[];
};

export const ContactReviewBoxPlatforms = ({
    platforms: platformsProp,
    media,
    ...props
}: ContactReviewBoxPlatformsProps) => {
    const context = useContext(ContactReviewBoxContext);
    const platforms: ReviewPlatform[] = platformsProp ?? context.platforms ?? [];

    return (
        <Layout className="contact__review-box__platforms" media={media} {...props}>
            {platforms.map((platform, index) => {
                const IconComponent = getReviewPlatformIcon(platform);
                return (
                    <IconComponent
                        key={index}
                        data-testid={`contact-review-platform-${platform.toLowerCase()}`}
                    />
                );
            })}
        </Layout>
    );
};

// Legacy export for backward compatibility
export const ContactReviewBox = ContactReviewBoxWrapper;

// Compound component structure
export const ContactReviewBoxCompound: typeof ContactReviewBoxWrapper & {
    Title: typeof ContactReviewBoxTitle;
    Divider: typeof ContactReviewBoxDivider;
    Platforms: typeof ContactReviewBoxPlatforms;
} = Object.assign(ContactReviewBoxWrapper, {
    Title: ContactReviewBoxTitle,
    Divider: ContactReviewBoxDivider,
    Platforms: ContactReviewBoxPlatforms,
});
